// Feature: docs/features/deterministic-rules-management.md
import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  FormHelperText,
  RadioGroup,
  Radio,
  HStack,
  Box
} from '@chakra-ui/react';

const RuleModal = ({ isOpen, onClose, onSave, availableActions, existingPayers = [], existingCarriers = [] }) => {
  const [formData, setFormData] = useState({
    rule_type: 'all-post',
    target_name: '',
    action: ''
  });

  // UI state for the "All Post" target selection
  const [postTargetMode, setPostTargetMode] = useState('ALL'); // 'ALL', 'EXCEPT', 'SPECIFIC'
  const [customTargetInput, setCustomTargetInput] = useState('');

  useEffect(() => {
    if (isOpen) {
      setFormData({
        rule_type: 'all-post',
        target_name: '',
        action: ''
      });
      setPostTargetMode('ALL');
      setCustomTargetInput('');
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Reset target inputs when changing rule type
    if (name === 'rule_type') {
      setCustomTargetInput('');
      if (value === 'all-pre') {
        setFormData(prev => ({ ...prev, target_name: 'ALL' }));
      } else if (value === 'all-post') {
        setPostTargetMode('ALL');
        setFormData(prev => ({ ...prev, target_name: 'ALL' }));
      } else {
        setFormData(prev => ({ ...prev, target_name: '' }));
      }
    }
  };

  const handleTargetInputChange = (e) => {
    const val = e.target.value;
    setCustomTargetInput(val);
    
    if (formData.rule_type === 'all-post' && postTargetMode === 'EXCEPT') {
      setFormData(prev => ({ ...prev, target_name: val ? `EXCLUDE:${val}` : '' }));
    } else {
      setFormData(prev => ({ ...prev, target_name: val }));
    }
  };

  const handlePostTargetModeChange = (val) => {
    setPostTargetMode(val);
    if (val === 'ALL') {
      setFormData(prev => ({ ...prev, target_name: 'ALL' }));
      setCustomTargetInput('');
    } else if (val === 'EXCEPT') {
      setFormData(prev => ({ ...prev, target_name: customTargetInput ? `EXCLUDE:${customTargetInput}` : '' }));
    } else if (val === 'SPECIFIC') {
      setFormData(prev => ({ ...prev, target_name: customTargetInput }));
    }
  };

  const handleSubmit = () => {
    // Ensure target_name is clean before sending
    const payload = { ...formData };
    if (payload.rule_type === 'all-pre') {
      payload.target_name = 'ALL';
    } else if (payload.rule_type === 'all-post' && postTargetMode === 'ALL') {
      payload.target_name = 'ALL';
    }
    
    onSave(payload);
  };

  const isFormValid = formData.rule_type && formData.action && 
    (formData.rule_type === 'all-pre' || 
     (formData.rule_type === 'all-post' && postTargetMode === 'ALL') || 
     formData.target_name.trim() !== '');

  // Group actions for better UX
  const groupedActions = {
    'Remove Codes': availableActions.filter(a => a.startsWith('remove_')),
    'Change Codes': availableActions.filter(a => a.startsWith('change_') && !a.includes('modifier')),
    'Apply Modifiers': availableActions.filter(a => a.startsWith('apply_') || a.includes('modifier')),
    'Cleanups': availableActions.filter(a => a.startsWith('cleanup_')),
    'Other': availableActions.filter(a => !a.startsWith('remove_') && !a.startsWith('change_') && !a.startsWith('apply_') && !a.startsWith('cleanup_') && !a.includes('modifier'))
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create New Rule</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={5} align="stretch">
            <FormControl isRequired>
              <FormLabel>Phase (Rule Type)</FormLabel>
              <Select 
                name="rule_type" 
                value={formData.rule_type} 
                onChange={handleChange}
              >
                <option value="all-pre">All Pre (Global Cleanups)</option>
                <option value="payer">Payer Specific</option>
                <option value="carrier">Carrier Specific</option>
                <option value="all-post">All Post (Modifiers/Pairs)</option>
              </Select>
              <FormHelperText>
                {formData.rule_type === 'all-pre' && "Runs first. Applies to all claims."}
                {formData.rule_type === 'payer' && "Runs second. Applies only to a specific payer."}
                {formData.rule_type === 'carrier' && "Runs third. Applies only to a specific carrier."}
                {formData.rule_type === 'all-post' && "Runs last. Used for NCCI modifiers and final checks."}
              </FormHelperText>
            </FormControl>

            {/* Dynamic Target Name Field */}
            {formData.rule_type !== 'all-pre' && (
              <FormControl isRequired>
                <FormLabel>Target Name</FormLabel>
                
                {formData.rule_type === 'all-post' && (
                  <RadioGroup value={postTargetMode} onChange={handlePostTargetModeChange} mb={3}>
                    <VStack align="start" spacing={2}>
                      <Radio value="ALL">Apply to ALL Payers</Radio>
                      <Radio value="EXCEPT">Apply to ALL EXCEPT a specific Payer</Radio>
                      <Radio value="SPECIFIC">Apply ONLY to a specific Payer</Radio>
                    </VStack>
                  </RadioGroup>
                )}

                {((formData.rule_type === 'all-post' && postTargetMode !== 'ALL') || 
                   formData.rule_type === 'payer' || 
                   formData.rule_type === 'carrier') && (
                  <Box>
                    {(formData.rule_type === 'payer' || (formData.rule_type === 'all-post' && postTargetMode !== 'ALL')) && existingPayers ? (
                      <Select
                        value={customTargetInput}
                        onChange={handleTargetInputChange}
                        placeholder="Select a Payer..."
                      >
                        {existingPayers.map(name => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </Select>
                    ) : (
                      <>
                        <Input 
                          value={customTargetInput} 
                          onChange={handleTargetInputChange}
                          placeholder={`Enter ${formData.rule_type === 'carrier' ? 'Carrier' : 'Payer'} Name...`}
                          list="target-suggestions"
                        />
                        <datalist id="target-suggestions">
                          {(formData.rule_type === 'carrier' ? existingCarriers : (existingPayers || [])).map(name => (
                            <option key={name} value={name} />
                          ))}
                        </datalist>
                      </>
                    )}
                    <FormHelperText>
                      {formData.rule_type === 'all-post' && postTargetMode === 'EXCEPT' 
                        ? "The system will automatically format this as an EXCLUDE rule."
                        : formData.rule_type === 'carrier' 
                          ? "Type a new carrier name or select from existing ones."
                          : existingPayers 
                            ? "Select a standard payer from the list."
                            : "Type a new payer name or select from existing ones."}
                    </FormHelperText>
                  </Box>
                )}
              </FormControl>
            )}

            <FormControl isRequired>
              <FormLabel>Action</FormLabel>
              <Select 
                name="action" 
                value={formData.action} 
                onChange={handleChange}
                placeholder="Select an action..."
              >
                {Object.entries(groupedActions).map(([groupName, actions]) => {
                  if (actions.length === 0) return null;
                  return (
                    <optgroup key={groupName} label={groupName}>
                      {actions.map(action => (
                        <option key={action} value={action}>
                          {action}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button 
            colorScheme="blue" 
            onClick={handleSubmit}
            isDisabled={!isFormValid}
          >
            Save Rule
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RuleModal;
